// frontend/src/pages/PaymentPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { paymentApi } from '../api/paymentApi';
import { useAuth } from '../context/AuthContext';
import { FaSpinner, FaExclamationTriangle, FaCreditCard, FaLock, FaCheckCircle } from 'react-icons/fa';

function PaymentPage() {
    const { invoiceId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [cards, setCards] = useState([]);
    const [selectedCard, setSelectedCard] = useState(null); // 'new' or token string

    useEffect(() => {
        const init = async () => {
            if (!user) {
                setError("You must be logged in to make a payment.");
                setLoading(false);
                return;
            }

            try {
                // Fetch saved cards
                const savedCards = await paymentApi.getSavedCards();
                setCards(savedCards || []);
                // Default to first card if available, else 'new'
                setSelectedCard(savedCards && savedCards.length > 0 ? savedCards[0].token : 'new');
            } catch (err) {
                console.error("Failed to load cards", err);
            } finally {
                setLoading(false);
            }
        };

        init();
    }, [user]);

    const handlePayment = async () => {
        setProcessing(true);
        setError(null);

        try {
            if (selectedCard === 'new') {
                // Standard Flow: Get Link & Redirect
                const response = await paymentApi.initiateServicePayment(invoiceId);
                if (response.payment_link) {
                    window.location.href = response.payment_link;
                } else {
                    throw new Error("Could not retrieve payment link.");
                }
            } else {
                // One-Click Flow: Charge Token
                const response = await paymentApi.chargeSavedCard(invoiceId, selectedCard);
                if (response.success) {
                    alert("Payment Successful!");
                    navigate('/account'); // Or success page
                } else {
                    throw new Error(response.message || "Payment failed.");
                }
            }
        } catch (err) {
            console.error("Payment failed:", err);
            setError(err.message || "An error occurred.");
            setProcessing(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[70vh] flex flex-col items-center justify-center text-center">
                <FaSpinner className="animate-spin text-primary text-4xl mb-4" />
                <p className="text-gray-500">Loading payment options...</p>
            </div>
        );
    }

    return (
        <div className="min-h-[70vh] flex flex-col items-center justify-center bg-gray-50 px-4">
            <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
                <h1 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                    <FaLock className="text-green-600" /> Secure Payment
                </h1>

                {error && (
                    <div className="bg-red-100 text-red-700 p-3 rounded mb-4 flex items-center gap-2">
                        <FaExclamationTriangle /> {error}
                    </div>
                )}

                <div className="space-y-4 mb-8">
                    <p className="text-gray-600 text-sm mb-2">Select Payment Method:</p>
                    
                    {/* Saved Cards */}
                    {cards.map((card, idx) => (
                        <label key={idx} className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${selectedCard === card.token ? 'border-primary bg-blue-50 ring-1 ring-primary' : 'border-gray-200 hover:bg-gray-50'}`}>
                            <input 
                                type="radio" 
                                name="payment_method" 
                                checked={selectedCard === card.token} 
                                onChange={() => setSelectedCard(card.token)}
                                className="h-5 w-5 text-primary"
                            />
                            <div className="ml-3">
                                <span className="block font-bold text-gray-800 flex items-center gap-2">
                                    <FaCreditCard /> {card.type.toUpperCase()} **** {card.last_4digits}
                                </span>
                                <span className="text-xs text-gray-500">Expires: {card.expiry}</span>
                            </div>
                        </label>
                    ))}

                    {/* New Card Option */}
                    <label className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${selectedCard === 'new' ? 'border-primary bg-blue-50 ring-1 ring-primary' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <input 
                            type="radio" 
                            name="payment_method" 
                            checked={selectedCard === 'new'} 
                            onChange={() => setSelectedCard('new')}
                            className="h-5 w-5 text-primary"
                        />
                        <div className="ml-3">
                            <span className="block font-bold text-gray-800">Pay with New Card / Transfer</span>
                            <span className="text-xs text-gray-500">You will be redirected to Flutterwave</span>
                        </div>
                    </label>
                </div>

                <button 
                    onClick={handlePayment}
                    disabled={processing}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg transition duration-300 flex items-center justify-center gap-2 shadow-md"
                >
                    {processing ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />} 
                    {selectedCard === 'new' ? 'Proceed to Payment' : 'Pay Instantly'}
                </button>
                
                <button onClick={() => navigate('/account')} className="mt-4 text-gray-500 hover:text-gray-700 text-sm w-full">
                    Cancel
                </button>
            </div>
        </div>
    );
}

export default PaymentPage;